import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import notifications from "../../common/Components/Notifications/Notification";
import { canUseRounds } from "../../helpers/karkhanaEnabled";
import { getOpeningBalances, removeOpeningBalance, saveOpeningBalances } from "../../services/karkhanaApi";
import "../gi-registry/gi-registry.scss";
import "./workers/karkhana-workers.scss";
import "./rounds/karkhana-rounds.scss";
import "./pay/karkhana-pay.scss";
import { LangToggle, useKT } from "./i18n/kt";
import PageHeader from "./ui/PageHeader";
import { kp } from "./karkhanaPaths";
import { fmtDay, rupees, todayIso } from "./rates/rateModel";
import { changedRows, missingCut, OpeningEdit, OpeningPerson, startEdit } from "./pay/openingModel";

const readRole = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role;
  } catch {
    return undefined;
  }
};

const digits = (v: string) => v.replace(/[^\d.]/g, "");

/**
 * Opening balances (owner only): what each person's account stood at on the morning the studio moved onto HKP, from
 * the old register: wages still owed to them and advance they still hold. From that date HKP counts on from these
 * figures; work and money entered before it are covered by the opening and no longer add up.
 */
const KarkhanaOpeningBalancesPage = () => {
  const { t } = useKT();
  const history = useHistory();
  const role = readRole();
  const owner = role === "ORGANIZATION_ADMIN";
  const [people, setPeople] = useState<OpeningPerson[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [edits, setEdits] = useState<Record<string, OpeningEdit>>({});
  const [date, setDate] = useState(todayIso());
  const [note, setNote] = useState(t("Old register"));
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setFailed(false);
    const res: any = await getOpeningBalances().catch(() => null);
    if (!res?.success) {
      setFailed(true);
      return;
    }
    const list: OpeningPerson[] = res.data.people || [];
    setPeople(list);
    setEdits(Object.fromEntries(list.map((p) => [p.id, startEdit(p)])));
    // The date already used, if any; else today.
    const used = list.map((p) => p.opening?.date).find(Boolean);
    setDate(used || res.data.today || todayIso());
  }, []);

  useEffect(() => {
    if (!canUseRounds(role) || !owner) {
      history.replace(kp.pay());
      return;
    }
    load();
  }, [role, owner, history, load]);

  const rows = useMemo(() => (people ? changedRows(people, edits, date) : []), [people, edits, date]);
  const blocked = people?.filter((p) => rows.some((r) => r.personId === p.id) && missingCut(edits[p.id])) || [];
  const shown = (people || []).filter((p) => {
    const s = q.trim().toLowerCase();
    return !s || p.code.toLowerCase().includes(s) || (p.name || "").toLowerCase().includes(s);
  });

  const set = (id: string, key: keyof OpeningEdit, v: string) => setEdits((e) => ({ ...e, [id]: { ...e[id], [key]: digits(v) } }));

  const save = async () => {
    if (!rows.length || blocked.length || !note.trim()) return;
    setBusy(true);
    const res: any = await saveOpeningBalances({ date, note: note.trim(), rows }).catch(() => null);
    setBusy(false);
    if (!res?.success) {
      notifications({ title: t("Not saved"), message: t(res?.message || "This needs a connection. Nothing was saved."), type: "danger" });
      return;
    }
    notifications({ title: t("Opening balances saved"), message: t("{{n}} accounts now start on {{day}}.", { n: res.data.saved, day: fmtDay(date) }), type: "success" });
    load();
  };

  const remove = async (p: OpeningPerson) => {
    const why = window.prompt(t("Why remove {{code}}'s opening balance? The account goes back to its full history.", { code: p.code }));
    if (!why || !why.trim()) return;
    const res: any = await removeOpeningBalance(p.id, why.trim()).catch(() => null);
    if (!res?.success) {
      notifications({ title: t("Not saved"), message: t(res?.message || "This needs a connection. Nothing was saved."), type: "danger" });
      return;
    }
    load();
  };

  if (!canUseRounds(role) || !owner) return null;

  return (
    <div className="gi-registry-page kw-page kr-rounds kob-page has-foot">
      <PageHeader title={t("Opening balances")} back={kp.pay()} backLabel={t("Pay")}>
        <LangToggle />
      </PageHeader>
      <p className="kw-lede">
        {t(
          "From your old register, type what each person's account stood at on the morning of this date. HKP counts on from these figures. Work and money entered before this date are covered by the opening and stop adding up."
        )}
      </p>

      <div className="kob-head kw-card">
        <label className="kr-label" htmlFor="kob-date">
          {t("Balances as of the start of")}
        </label>
        <input id="kob-date" type="date" className="kr-select" value={date} max={todayIso()} onChange={(e) => e.target.value && setDate(e.target.value)} />
        <label className="kr-label" htmlFor="kob-note">
          {t("Where the figures come from")}
        </label>
        <input id="kob-note" className="kr-select" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />
      </div>

      {failed ? (
        <div className="kr-banner is-warn">
          {t("Could not load the people. This needs a connection.")}{" "}
          <button type="button" className="btn btn-link btn-sm p-0" onClick={load}>
            {t("Try again")}
          </button>
        </div>
      ) : null}
      {!people && !failed ? <div className="kw-empty mt-3">{t("Loading…")}</div> : null}
      {people && !people.length ? <div className="kw-empty">{t("No workers yet. Add them in Workers first.")}</div> : null}

      {people && people.length ? (
        <>
          <input
            className="kr-select kob-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("Find by code or name, e.g. K_56")}
            aria-label={t("Find by code or name, e.g. K_56")}
          />
          <div className="kob-cols kw-muted" aria-hidden="true">
            <span>{t("Person")}</span>
            <span>{t("Wages owed to them")}</span>
            <span>{t("Advance they hold")}</span>
            <span>{t("Cut each payout")}</span>
          </div>
          <ul className="kob-list">
            {shown.map((p) => {
              const e = edits[p.id] || startEdit(p);
              const changed = rows.some((r) => r.personId === p.id);
              const cutMissing = changed && missingCut(e);
              return (
                <li key={p.id} className={`kob-row kw-card${changed ? " is-changed" : ""}`}>
                  <div className="kob-who">
                    <strong>
                      <span className="kw-code">{p.code}</span> {p.name}
                    </strong>
                    <span className="kw-muted">
                      {p.pendingConfirmation
                        ? t("Waiting for owner to confirm in Workers")
                        : p.opening
                          ? t("Opening set for {{day}}", { day: fmtDay(p.opening.date) })
                          : t("HKP shows: due {{due}} · advance {{adv}}", { due: rupees(p.wagesDue || 0, 0), adv: rupees(p.advanceOutstanding || 0, 0) })}
                    </span>
                    {p.opening ? (
                      <button type="button" className="btn btn-link btn-sm p-0 kob-remove" onClick={() => remove(p)}>
                        {t("Remove opening")}
                      </button>
                    ) : null}
                  </div>
                  <label className="kob-cell">
                    <span className="kob-cell__label">{t("Wages owed to them")}</span>
                    <input
                      className="kr-select"
                      inputMode="decimal"
                      placeholder="₹0"
                      disabled={p.pendingConfirmation}
                      value={e.wagesDue}
                      onChange={(ev) => set(p.id, "wagesDue", ev.target.value)}
                    />
                  </label>
                  <label className="kob-cell">
                    <span className="kob-cell__label">{t("Advance they hold")}</span>
                    <input
                      className="kr-select"
                      inputMode="decimal"
                      placeholder="₹0"
                      disabled={p.pendingConfirmation}
                      value={e.advance}
                      onChange={(ev) => set(p.id, "advance", ev.target.value)}
                    />
                  </label>
                  <label className="kob-cell">
                    <span className="kob-cell__label">{t("Cut each payout")}</span>
                    <input
                      className={`kr-select${cutMissing ? " is-invalid" : ""}`}
                      inputMode="decimal"
                      placeholder={Number(e.advance) > 0 ? t("needed") : "—"}
                      disabled={p.pendingConfirmation || !(Number(e.advance) > 0)}
                      value={e.recoveryPerPay}
                      aria-invalid={cutMissing}
                      onChange={(ev) => set(p.id, "recoveryPerPay", ev.target.value)}
                    />
                  </label>
                </li>
              );
            })}
          </ul>
        </>
      ) : null}

      <p className={`kob-status${blocked.length ? " is-warn" : ""}`} role="status">
        {blocked.length
          ? t("Enter the cut each payout for {{codes}}.", { codes: blocked.map((p) => p.code).join(", ") })
          : rows.length
            ? t("{{n}} to save, as of {{day}}", { n: rows.length, day: fmtDay(date) })
            : t("Nothing changed yet")}
      </p>
      <div className="kf-foot">
        <button type="button" className="kd-btn" disabled={busy || !rows.length || blocked.length > 0 || !note.trim()} onClick={save}>
          {busy ? t("Saving…") : t("Save opening balances")}
        </button>
      </div>
    </div>
  );
};

export default KarkhanaOpeningBalancesPage;
